/* eslint-disable no-param-reassign */
/**
 * HTTP 请求
 */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { isObject } from './common'

export interface RetDataType<T> {
  errno: number
  errmsg: string
  data: T
}

const instance = axios.create({
  // @ts-ignore
  baseURL: `${import.meta.env.VITE_SAFE_HOST}/api`,
  withCredentials: true,
})

/**
 * http层校验
 * @param {AxiosResponse} response
 * @returns
 */
function checkStatus(response: AxiosResponse) {
  return response.status >= 200 && response.status < 300 ? response : Promise.reject(response)
}

/**
 * 业务层校验
 * @param {RetDataType} response
 * @returns
 */
function checkResponse(response: RetDataType<any>) {
  response.errno = Number(response.errno)

  // 业务请求正常
  if (response.errno === 0) {
    return response
  }

  // 10012: token过期
  // 10000: cookie不存在
  // 10019: PIN不正确
  if (response.errno === 10012 || response.errno === 10000 || response.errno === 10019) {
    return Promise.reject(response)
  }
  return Promise.reject(response)
}

// 应用层后期可能需要拿到 response 进行额外的处理操作
function processError(response: RetDataType<any>) {
  return response
}

// POST 时 urlParam 的处理
// 如果 value 是数组 则 key要添加 '[]'
const handlePostReducer = (last: URLSearchParams, [key, value]: [string, unknown]) => {
  const isArray = Array.isArray(value)
  const usedKey = isArray ? `${key.replace(/\[]$/, '')}[]` : key
  if (isArray) {
    last = value.length
      ? value.reduce((l, item) => {
          l.append(usedKey, item)
          return l
        }, last)
      : last.append(usedKey, '')
  } else {
    const isObj = isObject(value)
    last.append(usedKey, (isObj ? JSON.stringify(value) : value) as string)
  }
  return last
}

/**
 * 基于 axios 二次封装的 request 方法，系统统一的网络请求方法
 * @param options 请求配置项目，具体查看 https://github.com/axios/axios
 * @returns Promise<RetDataType<T>>
 */
export function sendRequest<T>(
  options: AxiosRequestConfig,
  token: string,
  pin: string
): Promise<RetDataType<T>> {
  if (options.method === 'POST' || options.method === 'PUT') {
    if (options.params && !options.data) {
      options.data = options.params
      delete options.params
    }

    // 是 FormData 不再处理
    const isFormData = options.data instanceof FormData

    if (!isFormData && options.data && !options.data.$useRaw) {
      // 表现形式：windows 10 + chrome 63.0.3239.108
      // FormData 的数据格式下 Content-Type 会被强行转为 multipart/form-data
      // 因此使用 UrlSearchParams
      const params = new URLSearchParams()
      options.data = Object.entries(options.data).reduce(handlePostReducer, params)
    }
  }
  if (!options.headers) {
    options.headers = {}
  }
  options.headers.Authorization = token
  options.headers['Header-PIN'] = pin

  return instance
    .request(options)
    .then(checkStatus)
    .then((res) => res.data)
    .then(checkResponse)
    .catch(processError)
}
